/**
 * Human titles for the audited actions the tenant profile records.
 *
 * The Activity Timeline renders audit rows by diffing `new_values`, which reads
 * as a vague catch-all title or "Updated Key picked up at: 8 Aug → —". That is fine
 * for a free-form field edit and useless for a named action — nobody should
 * have to decode a column diff to learn that an admin undid a key pickup.
 *
 * Kept in its own module rather than inside the timeline builder so the set of
 * titles can be checked against the set of actions the dialog actually writes
 * (see tenant-audit-titles.test.ts). An action added without a title here is
 * the exact regression that leaves a bare column diff in the log.
 */

/** Suffix every reversal carries, so undo handling never relies on a list. */
export const REVERSAL_SUFFIX = "_undone";

export const AUDIT_ACTION_TITLES: Record<string, string> = {
  contract_marked_signed: "Contract marked signed",
  contract_marked_signed_undone: "Contract signature mark undone",
  key_marked_picked_up: "Key marked as picked up",
  key_marked_picked_up_undone: "Key pickup mark undone",
  key_marked_returned: "Key marked as returned",
  key_marked_returned_undone: "Key return mark undone",
  move_in_confirmed: "Move-in confirmed",
  move_in_confirmed_undone: "Move-in confirmation undone",
  move_in_protocol_recorded: "Move-in protocol recorded on tenant's behalf",
  move_out_protocol_recorded: "Move-out protocol recorded on tenant's behalf",
  move_in_protocol_replaced: "Move-in protocol document replaced",
  move_out_protocol_replaced: "Move-out protocol document replaced",
  move_in_protocol_reopened: "Move-in protocol reopened for tenant",
  move_in_protocol_reopened_undone: "Move-in protocol reopen undone",
  move_out_protocol_reopened: "Move-out protocol reopened for tenant",
  move_out_protocol_reopened_undone: "Move-out protocol reopen undone",
  move_in_protocol_closed: "Move-in protocol closed for tenant",
  move_in_protocol_closed_undone: "Move-in protocol close undone",
  move_out_protocol_closed: "Move-out protocol closed for tenant",
  move_out_protocol_closed_undone: "Move-out protocol close undone",
  move_in_protocol_reopen_stamp_cleared_on_downgrade:
    "Move-in protocol reopen stamp cleared (submission undone)",
  move_in_protocol_reopen_stamp_cleared_on_downgrade_undone:
    "Move-in protocol reopen stamp restoration undone",
  move_out_protocol_reopen_stamp_cleared_on_downgrade:
    "Move-out protocol reopen stamp cleared (submission undone)",
  move_out_protocol_reopen_stamp_cleared_on_downgrade_undone:
    "Move-out protocol reopen stamp restoration undone",
  move_in_protocol_submitted_by_admin: "Move-in protocol submitted by admin",
  move_out_protocol_submitted_by_admin: "Move-out protocol submitted by admin",
  move_out_initiated: "Move-out initiated",
  move_out_initiated_undone: "Move-out initiation undone",
  move_out_date_confirmed: "Move-out date confirmed",
  move_out_date_confirmed_undone: "Move-out date confirmation undone",
  deposit_marked_paid: "Deposit marked paid",
  deposit_marked_paid_undone: "Deposit payment mark undone",
  deposit_marked_unpaid: "Deposit marked unpaid",
  deposit_marked_unpaid_undone: "Deposit unpaid mark undone",
  stage_forced: "Stage forced (manual override)",
  stage_forced_undone: "Stage override undone",
  first_month_rent_override_set: "First-month rent override",
  booking_archived: "Booking archived",
  booking_restored: "Booking restored to pipeline",
  payment_proof_uploaded: "Payment proof uploaded",
  deposit_refund_marked_paid: "Deposit refund marked paid",
  moveout_bundle_assembled: "Move-out bundle prepared",
  moveout_bundle_sent: "Move-out bundle sent",
  deposit_deduction_added: "Deposit deduction added",
  deposit_deduction_updated: "Deposit deduction updated",
  deposit_deduction_deleted: "Deposit deduction removed",
  deposit_released: "Deposit decision recorded",
  // COL-360. A base-value change and a tenant discount are separate events with
  // different blast radius, so they get separate titles rather than collapsing
  // into the generic field-diff title.
  tenant_discount_set: "Tenant discount applied",
  tenant_discount_removed: "Tenant discount removed",
  room_base_synced_from_tenant: "Room base values updated from tenant",
  // Written by reassign_bookings_atomically (SQL), not by the dialog: the room
  // is the source of truth for base rent/deposit, so a move re-derives both
  // and the timeline should say so in one line rather than as a column diff.
  tenant_room_reassigned:
    "Moved to another room (rent and deposit follow the room)",
  tenant_money_resynced_to_room: "Rent and deposit re-synced to the room",
};

/**
 * Actions the timeline deliberately renders from their field diff instead of a
 * fixed title, because the interesting part IS which field changed.
 */
export const DIFF_RENDERED_ACTIONS = new Set([
  // A free-form edit from the manual-override panel: "Updated Monthly rent".
  "manual_override",
  // updateBookingRow's default, for writes with no action name of their own.
  "booking_updated",
  // Rendered by resource_type branches (document / payment) further up.
  "payment_edited",
  "payment_scheduled",
  "payment_proof_deleted",
  "tenant_document_renamed",
  "tenant_document_relabelled",
  "tenant_document_deleted",
  "tenant_document_note_added",
  "tenant_document_note_updated",
  "tenant_document_note_cleared",
  "tenant_document_uploaded",
  "rent_reminder_sent",
]);

/**
 * Titles for a field diff that touched several columns at once (an admin edit
 * form save, or a system job such as the 09:00 rent-reminder run). A bare
 * "Details updated" says nothing, so the changed columns are grouped into a few
 * topics and the title names them — five words at most, in either language.
 */
type ChangeTopic = {
  id: string;
  /** Matches a booking column. First topic that matches wins. */
  match: RegExp;
  /** One word: used when the title joins topics ("Rent and deposit updated"). */
  label: string;
  /** Used when the diff is about this topic alone. */
  single: string;
};

// Order matters twice: it picks the topic for a column that could fit two
// (contract_status is a contract matter, not a booking-status one), and it is
// the order topics are named in, most consequential first.
const CHANGE_TOPICS: ChangeTopic[] = [
  {
    id: "rent",
    // discount_reason / discount_set_at / discount_set_by are deliberately NOT
    // matched: they are shared metadata written on EVERY discount save (rent or
    // deposit, TenantEditPanel's hasDP block), so matching them here titled a
    // deposit-only discount edit "Rent and deposit updated". The value columns
    // (rent_discount_* / deposit_discount_*) are what carry the topic.
    match:
      /^(monthly_rent|first_month_rent_override|membership_fee|rent_discount_\w+|rent_due_date|rent_due_day_override)$/,
    label: "Rent",
    single: "Rent updated",
  },
  {
    id: "deposit",
    match: /^deposit_\w+$/,
    label: "Deposit",
    single: "Deposit updated",
  },
  {
    id: "contract",
    match:
      /^(contract_\w+|docusign_\w+|manual_contract_upload|signed_contract_url|wohnungsgeberbestaetigung_\w+|lease_signed_\w+)$/,
    label: "Contract",
    single: "Contract updated",
  },
  {
    id: "payment",
    match:
      /^(payment_status|preferred_payment_method|last_payment_received_at|rent_paid_through|manually_marked_paid)$/,
    label: "Payment",
    single: "Payment details updated",
  },
  {
    id: "reminders",
    // tenant_active is NOT a move-in fact: it backed the per-row
    // Active/Inactive toggle on the Reminders rows (removed in PR #1012; see
    // the note in useProperties.ts), so a flip of it belongs here.
    match:
      /^(reminder_\w+|reminders_active|accumulated_fees_eur|inkasso_\w+|rent_reminders_enabled|tenant_active)$/,
    label: "Reminders",
    single: "Reminder status updated",
  },
  {
    id: "moveIn",
    match: /^(move_in_\w+|actual_move_in_date|start_date)$/,
    label: "Move-in",
    single: "Move-in details updated",
  },
  {
    id: "moveOut",
    match: /^(move_out_\w+|actual_move_out_date|end_date)$/,
    label: "Move-out",
    single: "Move-out details updated",
  },
  {
    id: "room",
    match: /^(room_id|room_number|deleted_room_\w+)$/,
    label: "Room",
    single: "Room assignment updated",
  },
  {
    id: "key",
    match: /^(keynest_\w+|keys?_\w+)$/,
    label: "Key",
    single: "Key details updated",
  },
  {
    id: "status",
    match: /^(status|stage)$/,
    label: "Status",
    single: "Booking status updated",
  },
  {
    id: "archive",
    match: /^admin_archive\w*$/,
    label: "Archive",
    single: "Archive status updated",
  },
  {
    id: "contact",
    match: /^(email|phone)$/,
    label: "Contact",
    single: "Contact details updated",
  },
  {
    id: "name",
    match: /^full_name$/,
    label: "Name",
    single: "Name updated",
  },
  {
    id: "notes",
    match: /^(notes|description)$/,
    label: "Notes",
    single: "Notes updated",
  },
];

/** Topic ids for the given changed columns, most consequential first. */
export function changeTopicIds(keys: string[]): string[] {
  const hit = new Set<string>();
  for (const k of keys) {
    const topic = CHANGE_TOPICS.find((t) => t.match.test(k));
    if (topic) hit.add(topic.id);
  }
  return CHANGE_TOPICS.filter((t) => hit.has(t.id)).map((t) => t.id);
}

const changeKey = (suffix: string) =>
  `admin.tenantTabs.timeline.change.${suffix}`;

/**
 * Short title for a diff of `keys`. At most five words: one topic reads as
 * "Rent updated", two as "Rent and deposit updated", more as "Rent, deposit
 * and more updated". Columns that fit no topic fall back to a plain sentence.
 *
 * `lowerMid` lowercases a topic that is not first ("Rent and deposit…"); it is
 * off for German, where nouns keep their capital.
 */
export function describeChangedFields(
  keys: string[],
  tr: (key: string, fallback?: string) => string,
  lowerMid: boolean
): string {
  const ids = changeTopicIds(keys);
  const topic = (id: string) => CHANGE_TOPICS.find((t) => t.id === id)!;
  if (ids.length === 0)
    return tr(changeKey("generic"), "Booking details updated");
  if (ids.length === 1)
    return tr(changeKey(`${ids[0]}.single`), topic(ids[0]).single);
  const label = (id: string, first: boolean) => {
    const s = tr(changeKey(`${id}.label`), topic(id).label);
    return first || !lowerMid ? s : s.charAt(0).toLowerCase() + s.slice(1);
  };
  const a = label(ids[0], true);
  const b = label(ids[1], false);
  // One pass with a function replacement: labels come from tr() (a translator
  // can override them), and a chained .replace(string, label) would interpret
  // "$"-sequences in the label and let a label containing "{b}" swallow the
  // second placeholder.
  const fill = (template: string) =>
    template.replace(/\{a\}|\{b\}/g, (m) => (m === "{a}" ? a : b));
  return ids.length === 2
    ? fill(tr(changeKey("two"), "{a} and {b} updated"))
    : fill(tr(changeKey("many"), "{a}, {b} and more updated"));
}

/**
 * i18n key and English copy for each column the timeline labels by name, used
 * to build the single-column title ("Updated monthly rent") and the field-diff
 * rows.
 *
 * It lives here rather than inside the timeline builder so the German-fallback
 * guard test can derive its key set from this map. A hand-copied list in the
 * test still passes when a key is renamed or a column is added without German
 * copy — which is the exact regression the guard exists to catch, since a
 * missing entry renders a mixed-language title ("Aktualisiert Monthly rent").
 */
export const AUDIT_LABEL_KEYS: Record<string, { key: string; en: string }> = {
  full_name: { key: "admin.tenantTabs.timeline.auditFullName", en: "Name" },
  monthly_rent: {
    key: "admin.tenantTabs.timeline.auditMonthlyRent",
    en: "Monthly rent",
  },
  deposit_amount: {
    key: "admin.tenantTabs.timeline.auditDepositAmount",
    en: "Deposit",
  },
  move_in_date: { key: "admin.tenantTabs.timeline.auditMoveIn", en: "Move-in" },
  move_out_date: {
    key: "admin.tenantTabs.timeline.auditMoveOut",
    en: "Move-out",
  },
  actual_move_in_date: {
    key: "admin.tenantTabs.timeline.auditActualMoveIn",
    en: "Actual move-in",
  },
  actual_move_out_date: {
    key: "admin.tenantTabs.timeline.auditActualMoveOut",
    en: "Actual move-out",
  },
  contract_status: {
    key: "admin.tenantTabs.contractStatusLabel",
    en: "Contract status",
  },
  payment_status: {
    key: "admin.tenantTabs.timeline.auditPaymentStatus",
    en: "Payment status",
  },
  rent_reminders_enabled: {
    key: "admin.tenantTabs.timeline.auditRentReminders",
    en: "Rent reminders",
  },
  amount_cents: { key: "admin.tenantTabs.timeline.amount", en: "Amount" },
  due_date: { key: "admin.tenantTabs.timeline.auditDueDate", en: "Due date" },
  status: { key: "admin.tenantTabs.timeline.status", en: "Status" },
  rent_due_day_override: {
    key: "admin.tenantTabs.timeline.auditRentDueDay",
    en: "Rent due day",
  },
  // A day of the month (1-31) on the bookings row, same label as the override.
  rent_due_date: {
    key: "admin.tenantTabs.timeline.auditRentDueDay",
    en: "Rent due day",
  },
  first_month_rent_override: {
    key: "admin.tenantTabs.timeline.auditFirstMonthRent",
    en: "First-month rent",
  },
  notes: { key: "admin.tenantTabs.timeline.note", en: "Note" },
  file_name: {
    key: "admin.tenantTabs.timeline.auditFileName",
    en: "File name",
  },
  document_type: { key: "admin.tenantTabs.timeline.auditLabel", en: "Label" },
};

/**
 * The prefix on a single-column title. Carried here with the labels so the
 * guard test covers the whole German side of that sentence.
 */
export const AUDIT_UPDATED_PREFIX_KEY =
  "admin.tenantTabs.timeline.updatedPrefix";

/** Column -> display label, resolved through the caller's translator. */
export function auditLabels(
  tr: (key: string, fallback?: string) => string
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [column, { key, en }] of Object.entries(AUDIT_LABEL_KEYS))
    out[column] = tr(key, en);
  return out;
}

/**
 * Actions whose title is translated. Most titles above are English-only
 * constants; the two protocol-replacement titles are new in COL-555 and ship
 * with German, so the timeline resolves them through `t()` with the English
 * title here as the fallback. The rows live in the i18n migration.
 */
export const AUDIT_TITLE_KEYS: Record<string, string> = {
  move_in_protocol_replaced: "admin.tenantTabs.timeline.moveInProtocolReplaced",
  move_out_protocol_replaced:
    "admin.tenantTabs.timeline.moveOutProtocolReplaced",
};

/** The fixed title for an action, or null when it renders from its diff. */
export function auditActionTitle(action: string): string | null {
  return AUDIT_ACTION_TITLES[action] ?? null;
}

/** True when the action undid an earlier one. */
export function isReversalAction(action: string): boolean {
  return action.endsWith(REVERSAL_SUFFIX);
}
